/** Picks tab: competition matches, predictions, golden ball, and futures (winner / top scorer). */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { WORLD_CUP_COMPETITION_ID } from '../worldcup.constants';
import type { TournamentStageKey, WorldCupMatch } from '../types/worldcup.types';
import { buildSortedStageSections, WORLD_CUP_PICKS_STAGE_ORDER } from '../helpers/worldcupPicksSections.helper';
import { countryCodesFingerprintFromMatches } from '../helpers/flagCdn.helper';
import { prefetchFlagCdnForCountryCodes } from '../helpers/flagCdnPrefetch.helper';
import type { WorldCupPicksStageSection } from '../helpers/worldcupPicksSections.helper';
import {
  BACKEND_TO_FRONTEND_STAGE,
  FRONTEND_TO_BACKEND_STAGE,
  FRONTEND_TO_GOLDEN_BALL_ROUND,
  applyBoostOptimisticUpdate,
  areWorldCupFuturesLockedByFirstKickoff,
  isGoldenBallEligibleStage,
  mapApiMatch,
} from '../helpers/worldcupPicksData.helper';
import type {
  WorldCupFuturesOptions,
} from '../helpers/worldcupPicksData.helper';
import type { UserMatchesOverlayPayload, StaticMatchesPayload } from '../helpers/worldCupMatchesApi.helper';
import { revealWorldCupMonkeyPicks } from '../helpers/worldCupMatchesApi.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { isMemberRowQueryKey } from '../../competitions/helpers/memberRowInvalidation.helper';
import { sanitizeWorldCupFuturesPayloadForSave } from '../helpers/worldcupPicksData.helper';
import {
  countAutofillableMonkeyPicks,
  applyRevealedMonkeyPicks,
} from '../helpers/worldcupAutofillPicks.helper';
import { useWorldCupMatchesQuery } from './useWorldCupMatchesQuery';
import { useWorldCupPickPersistence } from './useWorldCupPickPersistence';
import { useWorldCupTournamentStandingsQuery } from './useWorldCupTournamentStandingsQuery';
import { buildGroupPositionMap } from '../helpers/worldCupGroupStageStandings.helper';
import { GROUP_STAGE_FRONTEND_KEYS } from '../helpers/worldcupPicksData.helper';

export interface MatchPredictionInput {
  home: string;
  away: string;
}

interface WorldCupPicksData {
  matches: WorldCupMatch[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  predictions: Record<string, MatchPredictionInput>;
  onPredictionChange: (matchId: string, home: string, away: string) => void;
  /** Count of still-editable monkey picks the user has not adopted/edited (drives the Autofill button). */
  autofillableMonkeyPickCount: number;
  /** Reveal + adopt all still-editable monkey match-score picks. Resolves to how many were revealed. */
  autofillMonkeyPicks: () => Promise<number>;
  upcomingStageSections: WorldCupPicksStageSection[];
  finishedStageSections: WorldCupPicksStageSection[];
  onToggleBoost: (stageKey: TournamentStageKey, matchId: string) => void;
  isBoosted: (stageKey: TournamentStageKey, matchId: string) => boolean;
  isBoostLockedByOtherMatch: (stageKey: TournamentStageKey, matchId: string) => boolean;
  /** False from the Round of 16 onward — the x2 is Group Stage + Round of 32 only (server-enforced). */
  isBoostEligible: (stageKey: TournamentStageKey) => boolean;
  futures: FuturesPick | null;
  futuresLocked: boolean;
  saveFutures: (
    winnerTeamId: string | null | undefined,
    topScorerPlayerId: string | null | undefined,
  ) => Promise<void>;
  futuresOptions: WorldCupFuturesOptions | null;
}

export interface FuturesPick {
  winnerTeamId: string | null;
  topScorerPlayerId: string | null;
  isMonkeyWinnerPick?: boolean;
  isMonkeyTopScorerPick?: boolean;
  winnerCorrect?: boolean | null;
  topScorerCorrect?: boolean | null;
}

export function useWorldCupPicksData(): WorldCupPicksData {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const { isSignedIn, isLoaded } = useSupabaseAuth();

  const [predictions, setPredictions] = useState<Record<string, MatchPredictionInput>>({});
  const [boostedMatchByStage, setBoostedMatchByStage] = useState<
    Partial<Record<TournamentStageKey, string>>
  >({});
  const [futures, setFutures] = useState<FuturesPick | null>(null);

  const canFetch = isLoaded && isSignedIn;
  const matchesQuery = useWorldCupMatchesQuery(WORLD_CUP_COMPETITION_ID, canFetch);
  const standingsQuery = useWorldCupTournamentStandingsQuery(WORLD_CUP_COMPETITION_ID, canFetch);

  const competitionMatchIdMap = useMemo(() => {
    const apiMatches = matchesQuery.data?.matches ?? [];
    const map: Record<string, string> = {};
    apiMatches.forEach((m) => {
      map[m.matchId] = m.competitionMatchId;
    });
    return map;
  }, [matchesQuery.data?.matches]);

  const getCanPersistMatch = useCallback(
    (matchId: string) => {
      const apiMatch = matchesQuery.data?.matches.find((match) => match.matchId === matchId);
      return Boolean(competitionMatchIdMap[matchId] && apiMatch?.isPickEditable !== false);
    },
    [competitionMatchIdMap, matchesQuery.data?.matches],
  );

  const persistenceEnabled = Boolean(isLoaded && isSignedIn);

  const { onPredictionChange } = useWorldCupPickPersistence({
    competitionId: WORLD_CUP_COMPETITION_ID,
    persistenceEnabled,
    predictions,
    setPredictions,
    queryClient,
    apiClientRef,
    getCanPersistMatch,
  });

  const groupPositionMap = useMemo(
    () => buildGroupPositionMap(standingsQuery.data?.groups ?? []),
    [standingsQuery.data?.groups],
  );

  const matches = useMemo(() => {
    if (!isLoaded || !isSignedIn) return [];
    const apiMatches = matchesQuery.data?.matches ?? [];
    return apiMatches.map((m) => {
      const mapped = mapApiMatch(m);
      if (!GROUP_STAGE_FRONTEND_KEYS.includes(mapped.stageKey)) return mapped;
      const homePos = groupPositionMap[m.homeCountryCode];
      const awayPos = groupPositionMap[m.awayCountryCode];
      return {
        ...mapped,
        groupLetter: mapped.groupLetter ?? homePos?.groupLetter,
        homeGroupPosition: homePos?.position,
        awayGroupPosition: awayPos?.position,
      };
    });
  }, [isLoaded, isSignedIn, matchesQuery.data?.matches, groupPositionMap]);

  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  const flagCountryCodesFingerprint = useMemo(
    () => countryCodesFingerprintFromMatches(matches),
    [matches],
  );

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setPredictions({});
      setBoostedMatchByStage({});
      setFutures(null);
      return;
    }
    // No merged payload yet (mid-refetch / cache cleared / GC): keep the last good
    // picks instead of flashing them blank — real data will sync when it arrives.
    if (!matchesQuery.data) return;

    const apiMatches = matchesQuery.data?.matches ?? [];
    // Per-round model: each assigned ball is keyed by match, and its UI round is derived from the
    // match's own stage + round — so the Group Stage's three independent balls never collapse.
    const goldenBalls = matchesQuery.data?.goldenBallsByMatch ?? {};
    const boostMap: Partial<Record<TournamentStageKey, string>> = {};
    for (const goldenBall of Object.values(goldenBalls)) {
      const match = apiMatches.find(
        (row) => row.competitionMatchId === goldenBall.competitionMatchId,
      );
      if (!match) continue;
      const stageMapper = BACKEND_TO_FRONTEND_STAGE[match.stage];
      const frontendStage = stageMapper ? stageMapper(match.round) : 'first_round';
      boostMap[frontendStage] = match.matchId;
    }
    setBoostedMatchByStage(boostMap);
    setFutures(matchesQuery.data?.futures ?? null);
  }, [isLoaded, isSignedIn, matchesQuery.data]);

  useEffect(() => {
    const codes = matchesRef.current.flatMap((m) => [m.homeCountryCode, m.awayCountryCode]);
    prefetchFlagCdnForCountryCodes(codes);
  }, [flagCountryCodesFingerprint]);

  const onToggleBoost = useCallback(async (stageKey: TournamentStageKey, matchId: string) => {
    // The x2 is only allowed through the Round of 32 (server rejects later stages with 400).
    // Guard here so an ineligible toggle never optimistically flips + snaps back.
    if (!isGoldenBallEligibleStage(stageKey)) return;
    const currentBoost = boostedMatchByStage[stageKey];
    const newBoostMatchId = currentBoost === matchId ? undefined : matchId;

    const backendStage = FRONTEND_TO_BACKEND_STAGE[stageKey];
    const round = FRONTEND_TO_GOLDEN_BALL_ROUND[stageKey];
    const competitionMatchId = newBoostMatchId ? competitionMatchIdMap[newBoostMatchId] : null;
    // The round being toggled may already hold a ball — that match's id is cleared from the cache.
    const previousCompetitionMatchId = currentBoost ? competitionMatchIdMap[currentBoost] : null;

    let previousBoostMap: Partial<Record<TournamentStageKey, string>> = {};
    setBoostedMatchByStage((prev) => {
      previousBoostMap = { ...prev };
      return applyBoostOptimisticUpdate(prev, stageKey, newBoostMatchId);
    });

    // Assigning without a server match id would send a clear — skip PUT (e.g. empty seed).
    if (newBoostMatchId && !competitionMatchId) {
      return;
    }

    try {
      await apiClientRef.current(
        API_ENDPOINTS.competitions.goldenBallPath(WORLD_CUP_COMPETITION_ID, backendStage),
        {
          method: 'PUT',
          data: { competitionMatchId, round },
        },
      );
      queryClient.setQueryData<UserMatchesOverlayPayload>(
        worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
        (prev) => {
          if (!prev) return prev;
          const nextByMatch = { ...(prev.goldenBallsByMatch ?? {}) };
          if (previousCompetitionMatchId) delete nextByMatch[previousCompetitionMatchId];
          if (competitionMatchId) {
            nextByMatch[competitionMatchId] = { competitionMatchId, locked: false };
          }
          return { ...prev, goldenBallsByMatch: nextByMatch };
        },
      );
      void queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.leaderboardAll(WORLD_CUP_COMPETITION_ID),
      });
      void queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groups(WORLD_CUP_COMPETITION_ID),
      });
    } catch {
      setBoostedMatchByStage(previousBoostMap);
    }
  }, [boostedMatchByStage, competitionMatchIdMap, queryClient]);

  const isBoosted = useCallback(
    (stageKey: TournamentStageKey, matchId: string) => boostedMatchByStage[stageKey] === matchId,
    [boostedMatchByStage],
  );

  const isBoostLockedByOtherMatch = useCallback(
    (stageKey: TournamentStageKey, matchId: string) => {
      const boostedMatchId = boostedMatchByStage[stageKey];
      return boostedMatchId != null && boostedMatchId !== matchId;
    },
    [boostedMatchByStage],
  );

  const isBoostEligible = useCallback(
    (stageKey: TournamentStageKey) => isGoldenBallEligibleStage(stageKey),
    [],
  );

  const upcomingStageSections = useMemo(
    () =>
      buildSortedStageSections(
        matches.filter((m) => m.status === 'upcoming' || m.status === 'live'),
        'asc',
      ),
    [matches],
  );

  const finishedStageSections = useMemo(
    () => buildSortedStageSections(
      matches.filter((m) => m.status === 'finished'),
      'desc',
      [...WORLD_CUP_PICKS_STAGE_ORDER].reverse(),
    ),
    [matches],
  );

  const futuresLocked = useMemo(
    () => {
      if (matchesQuery.data?.futuresEditable != null) {
        return !matchesQuery.data.futuresEditable;
      }
      return areWorldCupFuturesLockedByFirstKickoff(matches);
    },
    [matches, matchesQuery.data?.futuresEditable],
  );

  // Stickiness — retaining the last non-empty participantTeams so a transient empty
  // payload never blanks the winner — is handled centrally in useWorldCupMatchesQuery
  // so the leaderboard and profile rows inherit the same protection, not just this card.
  const futuresOptions = useMemo((): WorldCupFuturesOptions | null => {
    if (!isLoaded || !isSignedIn) return null;
    return matchesQuery.data?.futuresOptions ?? null;
  }, [isLoaded, isSignedIn, matchesQuery.data?.futuresOptions]);

  const saveFutures = useCallback(
    async (winnerTeamId: string | null | undefined, topScorerPlayerId: string | null | undefined) => {
      const current = queryClient.getQueryData<UserMatchesOverlayPayload>(
        worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
      )?.futures ?? futures;
      const { winnerTeamId: w, topScorerPlayerId: t } = sanitizeWorldCupFuturesPayloadForSave(
        winnerTeamId === undefined ? (current?.winnerTeamId ?? null) : winnerTeamId,
        topScorerPlayerId === undefined ? (current?.topScorerPlayerId ?? null) : topScorerPlayerId,
        futuresOptions,
      );
      setFutures((prev) => ({
        ...prev,
        winnerTeamId: w,
        topScorerPlayerId: t,
        isMonkeyWinnerPick: winnerTeamId === undefined ? (prev?.isMonkeyWinnerPick ?? false) : false,
        isMonkeyTopScorerPick: topScorerPlayerId === undefined ? (prev?.isMonkeyTopScorerPick ?? false) : false,
      }));
      const data: { winnerTeamId?: string | null; topScorerPlayerId?: string | null } = {};
      if (winnerTeamId !== undefined) data.winnerTeamId = w;
      if (topScorerPlayerId !== undefined) data.topScorerPlayerId = t;
      try {
        await apiClientRef.current(
          API_ENDPOINTS.competitions.futuresPath(WORLD_CUP_COMPETITION_ID),
          {
            method: 'PUT',
            data,
          },
        );
        queryClient.setQueryData<UserMatchesOverlayPayload>(
          worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
          (prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              futures: {
                winnerTeamId: w,
                topScorerPlayerId: t,
                isMonkeyWinnerPick: winnerTeamId === undefined
                  ? (prev.futures?.isMonkeyWinnerPick ?? false)
                  : false,
                isMonkeyTopScorerPick: topScorerPlayerId === undefined
                  ? (prev.futures?.isMonkeyTopScorerPick ?? false)
                  : false,
                winnerCorrect: prev.futures?.winnerCorrect ?? null,
                topScorerCorrect: prev.futures?.topScorerCorrect ?? null,
              },
            };
          },
        );
        // Boards draw the winner/scorer chip from the member row, not from this screen's
        // payload, so writing the overlay above is not enough: without this the squad
        // standings table keeps the previous pick for its full 3-minute stale window.
        void queryClient.invalidateQueries({
          predicate: (query) => isMemberRowQueryKey(query.queryKey as readonly unknown[]),
        });
      } catch (err) {
        // If the server rejected the winner as "not a participant", stale client
        // UUIDs are almost certainly the cause (e.g. post-reseed cache gap).
        // Await a fresh static refetch, remap the team by name to its new UUID,
        // and retry once before reverting.
        if (
          isAxiosError(err)
          && err.response?.status === 400
          && winnerTeamId !== undefined
          && winnerTeamId !== null
          && w !== null
        ) {
          await queryClient.refetchQueries({
            queryKey: worldCupQueryKeys.matchesStatic(WORLD_CUP_COMPETITION_ID),
          });
          const freshStatic = queryClient.getQueryData<StaticMatchesPayload>(
            worldCupQueryKeys.matchesStatic(WORLD_CUP_COMPETITION_ID),
          );
          const freshTeams = freshStatic?.futuresOptions?.participantTeams ?? [];
          const staleTeamName = futuresOptions?.participantTeams?.find((team) => team.id === w)?.name;
          const staleTeamNameNorm = staleTeamName?.trim().toLowerCase();
          const freshWinnerId = staleTeamNameNorm
            ? (freshTeams.find((team) => team.name.trim().toLowerCase() === staleTeamNameNorm)?.id ?? null)
            : null;
          if (freshWinnerId && freshWinnerId !== w) {
            const retryData: { winnerTeamId?: string | null; topScorerPlayerId?: string | null } = {};
            if (winnerTeamId !== undefined) retryData.winnerTeamId = freshWinnerId;
            if (topScorerPlayerId !== undefined) retryData.topScorerPlayerId = t;
            try {
              await apiClientRef.current(
                API_ENDPOINTS.competitions.futuresPath(WORLD_CUP_COMPETITION_ID),
                { method: 'PUT', data: retryData },
              );
              setFutures((prev) => ({
                ...prev,
                winnerTeamId: freshWinnerId,
                topScorerPlayerId: t,
                isMonkeyWinnerPick: false,
                isMonkeyTopScorerPick: topScorerPlayerId === undefined
                  ? (prev?.isMonkeyTopScorerPick ?? false)
                  : false,
              }));
              queryClient.setQueryData<UserMatchesOverlayPayload>(
                worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
                (prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    futures: {
                      winnerTeamId: freshWinnerId,
                      topScorerPlayerId: topScorerPlayerId !== undefined
                        ? t
                        : (prev.futures?.topScorerPlayerId ?? null),
                      isMonkeyWinnerPick: false,
                      isMonkeyTopScorerPick: topScorerPlayerId === undefined
                        ? (prev.futures?.isMonkeyTopScorerPick ?? false)
                        : false,
                      winnerCorrect: prev.futures?.winnerCorrect ?? null,
                      topScorerCorrect: prev.futures?.topScorerCorrect ?? null,
                    },
                  };
                },
              );
              void queryClient.invalidateQueries({
                predicate: (query) => isMemberRowQueryKey(query.queryKey as readonly unknown[]),
              });
              return;
            } catch { /* retry failed — fall through to revert */ }
          }
        }
        const cached = queryClient.getQueryData<UserMatchesOverlayPayload>(
          worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
        );
        setFutures(cached?.futures ?? null);
        void queryClient.invalidateQueries({
          queryKey: worldCupQueryKeys.matchesStatic(WORLD_CUP_COMPETITION_ID),
        });
      }
    },
    [futures, futuresOptions, queryClient],
  );

  const autofillableMonkeyPickCount = useMemo(
    () => countAutofillableMonkeyPicks(matches, predictions),
    [matches, predictions],
  );

  const autofillMonkeyPicks = useCallback(async (): Promise<number> => {
    const { revealedMatchIds } = await revealWorldCupMonkeyPicks(
      apiClientRef.current,
      WORLD_CUP_COMPETITION_ID,
    );
    if (revealedMatchIds.length > 0) {
      queryClient.setQueryData<UserMatchesOverlayPayload>(
        worldCupQueryKeys.matchesMe(WORLD_CUP_COMPETITION_ID),
        (prev) => applyRevealedMonkeyPicks(prev, revealedMatchIds),
      );
    }
    return revealedMatchIds.length;
  }, [queryClient]);

  const isLoading = !isLoaded || (isSignedIn && matchesQuery.isLoading);
  const isFetching = isSignedIn && matchesQuery.isFetching;
  const isError = Boolean(isSignedIn && matchesQuery.isError);

  return {
    matches,
    isLoading,
    isFetching,
    isError,
    predictions,
    onPredictionChange,
    autofillableMonkeyPickCount,
    autofillMonkeyPicks,
    upcomingStageSections,
    finishedStageSections,
    onToggleBoost,
    isBoosted,
    isBoostLockedByOtherMatch,
    isBoostEligible,
    futures,
    futuresLocked,
    saveFutures,
    futuresOptions,
  };
}
